import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, ArrowsClockwise, Cards, CheckCircle, PencilSimple } from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { Alert, Animated, Dimensions, PanResponder, Platform, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber400: '#FBBF24',
  amber100: '#FEF3C7',
  amber50: '#FFFBEB',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald400: '#34D399',
  emerald100: '#D1FAE5',
  emerald50: '#ECFDF5',
  emerald700: '#065F46',
  purple600: '#7C3AED',
};

const showAlert = (title: string, message: string, buttons?: any[]) => {
  if (isWeb) {
    window.alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message, buttons);
  }
};

interface Flashcard {
  id: string;
  front: string;
  back: string;
  ordinal: number;
}

interface FlashcardSet {
  id: string;
  title: string | null;
  subject: string | null;
}

export default function FlashcardsDetailScreen() {
  const { id } = useLocalSearchParams();
  const [flashcardSet, setFlashcardSet] = useState<FlashcardSet | null>(null);
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [knownCount, setKnownCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [loading, setLoading] = useState(true);

  // État pour l'édition du titre
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // Animations
  const flipAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const resultScaleAnim = useRef(new Animated.Value(0)).current;

  // Interpolations pour le flip 3D
  const frontInterpolate = flipAnim.interpolate({
    inputRange: [0, 180],
    outputRange: ['0deg', '180deg'],
  });
  const backInterpolate = flipAnim.interpolate({
    inputRange: [0, 180],
    outputRange: ['180deg', '360deg'],
  });
  const frontOpacity = flipAnim.interpolate({
    inputRange: [89, 90],
    outputRange: [1, 0],
  });
  const backOpacity = flipAnim.interpolate({
    inputRange: [89, 90],
    outputRange: [0, 1],
  });

  useEffect(() => {
    fetchFlashcards();
  }, [id]);

  useEffect(() => {
    if (flashcards.length > 0) {
      Animated.timing(progressAnim, {
        toValue: ((currentIndex + 1) / flashcards.length) * 100,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  }, [currentIndex, flashcards.length]);

  const fetchFlashcards = async () => {
    try {
      const [setResult, cardsResult] = await Promise.all([
        supabase
          .from('flashcard_sets')
          .select('id, title, subject')
          .eq('id', id)
          .single(),
        supabase
          .from('flashcards')
          .select('id, front, back, ordinal')
          .eq('set_id', id)
          .order('ordinal', { ascending: true }),
      ]);

      if (setResult.data) {
        setFlashcardSet(setResult.data);
        setEditedTitle(setResult.data.title || '');
      }
      if (cardsResult.data && cardsResult.data.length > 0) {
        setFlashcards(cardsResult.data);
      }
    } catch (error) {
      console.error('Error fetching flashcards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTitle = () => {
    setEditedTitle(flashcardSet?.title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim() || !flashcardSet) return;
    
    setIsSavingTitle(true);
    try {
      const { error } = await supabase
        .from('flashcard_sets')
        .update({ title: editedTitle.trim() })
        .eq('id', flashcardSet.id);

      if (error) throw error;

      setFlashcardSet({ ...flashcardSet, title: editedTitle.trim() });
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Error saving title:', error);
      showAlert('Erreur', 'Impossible de sauvegarder le titre');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedTitle(flashcardSet?.title || '');
    setIsEditingTitle(false);
  };

  const flipCard = () => {
    const toValue = isFlipped ? 0 : 180;
    Animated.spring(flipAnim, {
      toValue,
      friction: 8,
      tension: 10,
      useNativeDriver: true,
    }).start();
    setIsFlipped(!isFlipped);
  };

  const resetCardAnimations = () => {
    slideAnim.setValue(0);
    scaleAnim.setValue(1);
    flipAnim.setValue(0);
    setIsFlipped(false);
  };

  const handleKnown = () => {
    const newKnownCount = knownCount + 1;
    setKnownCount(newKnownCount);
    
    const toValue = SCREEN_WIDTH;
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start(() => {
      if (currentIndex < flashcards.length - 1) {
        setCurrentIndex(prev => prev + 1);
        resetCardAnimations();
      } else {
        setIsFinished(true);
        Animated.spring(resultScaleAnim, {
          toValue: 1,
          friction: 4,
          tension: 40,
          useNativeDriver: true,
        }).start();
      }
    });
  };

  const handleReview = () => {
    const newReviewCount = reviewCount + 1;
    setReviewCount(newReviewCount);
    
    const toValue = -SCREEN_WIDTH;
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start(() => {
      if (currentIndex < flashcards.length - 1) {
        setCurrentIndex(prev => prev + 1);
        resetCardAnimations();
      } else {
        setIsFinished(true);
        Animated.spring(resultScaleAnim, {
          toValue: 1,
          friction: 4,
          tension: 40,
          useNativeDriver: true,
        }).start();
      }
    });
  };

  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      handleKnown();
    } else {
      handleReview();
    }
  };

  const handleRestart = () => {
    resultScaleAnim.setValue(0);
    progressAnim.setValue(0);
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownCount(0);
    setReviewCount(0);
    setIsFinished(false);
    resetCardAnimations();
  };

  const panResponder = PanResponder.create({
    onMoveShouldSetPanResponder: (_, gestureState) => {
      return Math.abs(gestureState.dx) > 10;
    },
    onPanResponderMove: (_, gestureState) => {
      slideAnim.setValue(gestureState.dx);
    },
    onPanResponderRelease: (_, gestureState) => {
      if (gestureState.dx > 100) {
        handleSwipe('right');
      } else if (gestureState.dx < -100) {
        handleSwipe('left');
      } else {
        Animated.spring(slideAnim, {
          toValue: 0,
          useNativeDriver: true,
        }).start();
      }
    },
  });

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.emerald500} />
        <Text style={styles.loadingText}>Chargement des flashcards...</Text>
      </View>
    );
  }

  if (!flashcardSet || flashcards.length === 0) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          <LinearGradient
            colors={[colors.emerald500, colors.emerald600]}
            style={styles.headerGradient}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerContent}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerTitleStatic}>Flashcards</Text>
                <View style={{ width: 40 }} />
              </View>
            </View>
          </LinearGradient>
          <View style={[styles.centered, { flex: 1 }]}>
            <LinearGradient
              colors={[colors.emerald100, colors.slate50]}
              style={styles.emptyIconContainer}
            >
              <Cards size={48} color={colors.emerald500} weight="duotone" />
            </LinearGradient>
            <Text style={styles.emptyTitle}>Flashcards introuvables</Text>
            <Text style={styles.emptySubtitle}>Ce set n'existe pas ou n'a pas de cartes.</Text>
            <TouchableOpacity style={styles.emptyButton} onPress={() => router.back()}>
              <Text style={styles.emptyButtonText}>Retour</Text>
            </TouchableOpacity>
          </View>
        </View>
      </>
    );
  }

  // Écran de résultat
  if (isFinished) {
    const totalCards = flashcards.length;
    const knownPercent = Math.round((knownCount / totalCards) * 100);
    
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          <LinearGradient
            colors={[colors.emerald500, colors.emerald600]}
            style={styles.headerGradient}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerContent}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerTitleStatic}>Terminé !</Text>
                <View style={{ width: 40 }} />
              </View>
            </View>
          </LinearGradient>
          
          <View style={[styles.centered, { flex: 1, padding: 24 }]}>
            <Animated.View style={[
              styles.resultCard,
              { transform: [{ scale: resultScaleAnim }] }
            ]}>
              <LinearGradient
                colors={[colors.emerald100, colors.emerald500 + '20']}
                style={styles.resultEmojiContainer}
              >
                <Text style={styles.resultEmoji}>🎉</Text>
              </LinearGradient>
              
              <Text style={styles.resultTitle}>Session terminée !</Text>
              
              <View style={styles.statsContainer}>
                <View style={styles.statBox}>
                  <LinearGradient
                    colors={[colors.emerald100, colors.emerald500 + '30']}
                    style={styles.statIconContainer}
                  >
                    <CheckCircle size={28} color={colors.emerald500} weight="duotone" />
                  </LinearGradient>
                  <Text style={styles.statValue}>{knownCount}</Text>
                  <Text style={styles.statLabel}>Maîtrisées</Text>
                </View>
                
                <View style={styles.statDivider} />
                
                <View style={styles.statBox}>
                  <LinearGradient
                    colors={[colors.amber100, colors.amber500 + '30']}
                    style={styles.statIconContainer}
                  >
                    <ArrowsClockwise size={28} color={colors.amber500} weight="duotone" />
                  </LinearGradient>
                  <Text style={styles.statValue}>{reviewCount}</Text>
                  <Text style={styles.statLabel}>À revoir</Text>
                </View>
              </View>
              
              <View style={styles.progressResult}>
                <View style={styles.progressResultBar}>
                  <LinearGradient
                    colors={[colors.emerald500, colors.emerald600]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[styles.progressResultFill, { width: `${knownPercent}%` }]}
                  />
                </View>
                <Text style={styles.progressResultText}>{knownPercent}% maîtrisé</Text>
              </View>
              
              <View style={styles.resultButtons}>
                <TouchableOpacity 
                  style={styles.primaryButton}
                  onPress={handleRestart}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.emerald500, colors.emerald600]}
                    style={styles.primaryButtonGradient}
                  >
                    <ArrowsClockwise size={20} color={colors.white} weight="bold" />
                    <Text style={styles.primaryButtonText}>Recommencer</Text>
                  </LinearGradient>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={styles.secondaryButton}
                  onPress={() => router.back()}
                  activeOpacity={0.8}
                >
                  <ArrowLeft size={18} color={colors.slate600} weight="bold" />
                  <Text style={styles.secondaryButtonText}>Retour aux révisions</Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
          </View>
        </View>
      </>
    );
  }

  const currentCard = flashcards[currentIndex];

  if (!currentCard) {
    return (
      <View style={[styles.container, styles.centered]}>
        <Text>Erreur: carte introuvable</Text>
        <TouchableOpacity style={styles.emptyButton} onPress={() => router.back()}>
          <Text style={styles.emptyButtonText}>Retour</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        {/* Header simplifié avec titre éditable */}
        <LinearGradient
          colors={[colors.emerald500, colors.emerald600]}
          style={styles.headerGradient}
        >
          <View style={styles.headerInner}>
            <View style={styles.headerContent}>
              <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                <ArrowLeft size={24} color={colors.white} weight="bold" />
              </TouchableOpacity>
              
              {/* Titre éditable directement dans le header */}
              {isEditingTitle ? (
                <View style={styles.headerEditContainer}>
                  <TextInput
                    style={styles.headerEditInput}
                    value={editedTitle}
                    onChangeText={setEditedTitle}
                    placeholder="Titre"
                    placeholderTextColor="rgba(255,255,255,0.6)"
                    autoFocus={!isWeb}
                    selectTextOnFocus
                  />
                  <TouchableOpacity 
                    style={styles.headerSaveButton} 
                    onPress={handleSaveTitle}
                    disabled={!editedTitle.trim() || isSavingTitle}
                  >
                    {isSavingTitle ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Text style={styles.headerSaveButtonText}>OK</Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.headerCancelButton} onPress={handleCancelEdit}>
                    <Text style={styles.headerCancelButtonText}>✕</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity onPress={handleEditTitle} style={styles.headerTitleContainer}>
                  <Text style={styles.headerTitle} numberOfLines={1}>
                    {flashcardSet.title || 'Flashcards'}
                  </Text>
                  <PencilSimple size={16} color="rgba(255,255,255,0.7)" weight="bold" />
                </TouchableOpacity>
              )}
              
              <View style={{ width: 40 }} />
            </View>
          </View>
        </LinearGradient>

        {/* Contenu principal */}
        <View style={styles.mainContent}>
          <View style={styles.innerContainer}>
            {/* Progress compact */}
            <View style={styles.progressContainer}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressLabel}>{currentIndex + 1} / {flashcards.length}</Text>
                <View style={styles.statsRow}>
                  <View style={styles.statBadge}>
                    <CheckCircle size={14} color={colors.emerald500} weight="bold" />
                    <Text style={styles.knownStat}>{knownCount}</Text>
                  </View>
                  <View style={[styles.statBadge, styles.statBadgeAmber]}>
                    <ArrowsClockwise size={14} color={colors.amber500} weight="bold" />
                    <Text style={styles.reviewStat}>{reviewCount}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.progressBar}>
                <Animated.View 
                  style={[
                    styles.progressFill,
                    { 
                      width: progressAnim.interpolate({
                        inputRange: [0, 100],
                        outputRange: ['0%', '100%'],
                      })
                    }
                  ]} 
                />
              </View>
            </View>

            {/* Card area - simplifié */}
            <View style={styles.cardArea}>
              <Animated.View
                {...panResponder.panHandlers}
                style={[
                  styles.cardContainer,
                  {
                    transform: [
                      { translateX: slideAnim },
                      { scale: scaleAnim },
                    ],
                  },
                ]}
              >
                <TouchableOpacity onPress={flipCard} activeOpacity={0.95} style={styles.cardTouchable}>
                  {/* Front - Question */}
                  <Animated.View style={[
                    styles.card,
                    styles.cardFront,
                    {
                      transform: [{ rotateY: frontInterpolate }],
                      opacity: frontOpacity,
                    },
                  ]}>
                    <Text style={styles.cardText}>{currentCard.front}</Text>
                    <Text style={styles.tapHint}>Toucher pour voir la réponse</Text>
                  </Animated.View>

                  {/* Back - Réponse */}
                  <Animated.View style={[
                    styles.card,
                    styles.cardBack,
                    {
                      transform: [{ rotateY: backInterpolate }],
                      opacity: backOpacity,
                    },
                  ]}>
                    <Text style={[styles.cardText, styles.cardTextBack]}>{currentCard.back}</Text>
                    <Text style={[styles.tapHint, styles.tapHintBack]}>Swipe ou utilise les boutons</Text>
                  </Animated.View>
                </TouchableOpacity>
              </Animated.View>
            </View>

            {/* Action buttons - plus discrets */}
            <View style={styles.actionsContainer}>
              <TouchableOpacity 
                style={styles.reviewButton}
                onPress={handleReview}
                activeOpacity={0.8}
              >
                <ArrowsClockwise size={22} color={colors.amber500} weight="bold" />
                <Text style={styles.reviewButtonText}>À revoir</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.knownButton}
                onPress={handleKnown}
                activeOpacity={0.8}
              >
                <CheckCircle size={22} color={colors.emerald500} weight="bold" />
                <Text style={styles.knownButtonText}>Je sais</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 15,
  },

  // Header
  headerGradient: {
    paddingTop: isWeb ? 24 : 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    ...(isWeb && { alignItems: 'center' }),
  },
  headerInner: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && { cursor: 'pointer' }),
  },
  headerTitleStatic: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
    textAlign: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginHorizontal: 12,
    ...(isWeb && { cursor: 'pointer' }),
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
  },
  headerEditContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
    gap: 8,
  },
  headerEditInput: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    textAlign: 'center',
  },
  headerSaveButton: {
    backgroundColor: 'rgba(255,255,255,0.3)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    ...(isWeb && { cursor: 'pointer' }),
  },
  headerSaveButtonText: {
    color: colors.white,
    fontWeight: '700',
    fontSize: 14,
  },
  headerCancelButton: {
    padding: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  headerCancelButtonText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 16,
    fontWeight: '600',
  },

  // Main content
  mainContent: {
    flex: 1,
    ...(isWeb && { alignItems: 'center' }),
  },
  innerContainer: {
    flex: 1,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },

  // Progress - compact
  progressContainer: {
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: colors.white,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  progressLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.slate700,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  statBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.emerald50,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 10,
  },
  statBadgeAmber: {
    backgroundColor: colors.amber50,
  },
  knownStat: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.emerald600,
  },
  reviewStat: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.amber500,
  },
  progressBar: {
    height: 6,
    backgroundColor: colors.slate200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.emerald500,
    borderRadius: 3,
  },

  // Card area - simplifié
  cardArea: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  cardContainer: {
    width: isWeb ? '100%' : SCREEN_WIDTH - 40,
    maxWidth: isWeb ? 480 : undefined,
    height: 320,
  },
  cardTouchable: {
    width: '100%',
    height: '100%',
    ...(isWeb && { cursor: 'pointer' }),
  },
  card: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: 24,
    padding: 28,
    justifyContent: 'center',
    alignItems: 'center',
    backfaceVisibility: 'hidden',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.1,
    shadowRadius: 20,
    elevation: 8,
  },
  cardFront: {
    backgroundColor: colors.white,
    borderWidth: 2,
    borderColor: colors.emerald400,
  },
  cardBack: {
    backgroundColor: colors.emerald500,
  },
  cardText: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.slate900,
    textAlign: 'center',
    lineHeight: 30,
    paddingHorizontal: 8,
  },
  cardTextBack: {
    color: colors.white,
  },
  tapHint: {
    position: 'absolute',
    bottom: 20,
    fontSize: 13,
    color: colors.slate400,
  },
  tapHintBack: {
    color: 'rgba(255,255,255,0.7)',
  },

  // Actions - plus compact
  actionsContainer: {
    flexDirection: 'row',
    padding: 16,
    paddingBottom: isWeb ? 16 : 24,
    gap: 12,
    backgroundColor: colors.white,
    borderTopWidth: 1,
    borderTopColor: colors.slate200,
  },
  reviewButton: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.amber50,
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.amber400,
    gap: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  knownButton: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.emerald50,
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.emerald400,
    gap: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  reviewButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.amber500,
  },
  knownButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.emerald600,
  },

  // Empty state
  emptyIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  emptyButton: {
    marginTop: 24,
    backgroundColor: colors.emerald500,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    ...(isWeb && { cursor: 'pointer' }),
  },
  emptyButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },

  // Result card
  resultCard: {
    backgroundColor: colors.white,
    borderRadius: 28,
    padding: 32,
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 8,
  },
  resultEmojiContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  resultEmoji: {
    fontSize: 56,
  },
  resultTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 24,
  },
  statsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    width: '100%',
  },
  statBox: {
    flex: 1,
    alignItems: 'center',
  },
  statIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.slate900,
  },
  statLabel: {
    fontSize: 14,
    color: colors.slate500,
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    height: 80,
    backgroundColor: colors.slate200,
  },
  progressResult: {
    width: '100%',
    marginBottom: 28,
  },
  progressResultBar: {
    height: 12,
    backgroundColor: colors.slate200,
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressResultFill: {
    height: '100%',
    borderRadius: 6,
  },
  progressResultText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.emerald500,
    textAlign: 'center',
  },
  resultButtons: {
    width: '100%',
    gap: 12,
  },
  primaryButton: {
    borderRadius: 16,
    overflow: 'hidden',
    ...(isWeb && { cursor: 'pointer' }),
  },
  primaryButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 8,
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
    padding: 16,
    backgroundColor: colors.slate100,
    gap: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  secondaryButtonText: {
    color: colors.slate600,
    fontSize: 16,
    fontWeight: '600',
  },
});
